/**
 * APMT Incident & Operations
 * Bowler-chart-style weekly/monthly KPI tracker for APMT terminals.
 *
 * Tab 1 – Operations KPIs  : weekly bowler table (last 8 ISO weeks, current highlighted)
 *   - Major Incident Management rows (P1/P2 count, Cumulative Downtime, MTTR Median)
 *     are fetched live from: GET /api/apmt/major-incident-kpis?start=&end=
 *   - Other rows remain static until their backend endpoints are ready.
 *
 * Tab 2 – Terminal Availability : monthly view by region (YTD + Jan–Apr + Current Week)
 *   NOTE: terminal data is still static – replace TERMINAL_DATA with an API call
 *         when GET /api/apmt/terminal-availability is available.
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton,
} from '@mui/material';
import Refresh from '@mui/icons-material/Refresh';
import { authFetch } from '../services/authFetch';
import { API_BASE_URL } from '../apiClient';

// ─── Types ────────────────────────────────────────────────────────────────────

type CellValue = number | string | null;

interface KPIRow {
  /** Visible group label in the "Functions" column */
  functionName: string;
  /** Visible metric description */
  metric: string;
  /** Target label to display */
  targetLabel: string;
  /** Unit type used for colour logic */
  unit: 'percent' | 'count' | 'mins' | 'hours' | 'text';
  /** Numeric target for colour comparison (null = no colouring) */
  targetValue: number | null;
  /** 'down' = lower is better, 'up' = higher is better */
  direction: 'up' | 'down';
  /**
   * Weekly values aligned to weekCols (one entry per column).
   * For MIM rows these are overridden by live API data;
   * for other rows they are static placeholders.
   */
  values: CellValue[];
  /** If set, this row's values are sourced from the MIM KPI API */
  mimKey?: 'p1p2_count' | 'cumulative_downtime_mins' | 'mttr_median_hours' | 'pct_caused_by_change';
  /** If set, this row's values are sourced from the asset-change-metrics API */
  msaKey?: 'mvc_certification_pct_portfolio';
  /** If set, this row's values are sourced from the terminal-availability-weekly API */
  termAvailKey?: true;
}

/** Single week period returned by the API */
interface MIMKPIPoint {
  /** Monday of the ISO week, e.g. "2026-02-09" */
  period_start: string;
  p1p2_count: number;
  cumulative_downtime_mins: number;
  mttr_median_hours: number | null;
  caused_by_change_count: number;
  pct_caused_by_change: number | null;
}

/** One column in the dynamic week header */
interface WeekColumn {
  /** Display label, e.g. "Week 7" or "Current Week 16" */
  headerLabel: string;
  /** Monday ISO date of this week, e.g. "2026-02-10" – used to match API response */
  mondayISO: string;
  /** ISO week number */
  weekNum: number;
  /** true for the rightmost column (current week) */
  current: boolean;
}

/** Terminal row returned by GET /api/apmt/terminal-availability */
interface AvailTerminal {
  id: number;
  site_code: string;
  display_name: string;
  region: string;
  sort_order: number;
  /** Availability % per period, aligned to periods array */
  availabilities: (number | null)[];
}

/** Period descriptor returned by GET /api/apmt/terminal-availability */
interface AvailPeriod {
  label: string;
  current: boolean;
}

/** Single week point returned by /api/apmt/terminal-availability-weekly */
interface TermAvailWeekPoint {
  period_start: string;          // Monday ISO date e.g. "2025-12-29"
  week_num: number;
  total_terminals: number;
  terminals_meeting_target: number;
  pct_meeting_target: number | null;
}

/** Single item in the asset-change-metrics API response */
interface AssetChangeMetricItem {
  portfolio: string;
  period_type: string;
  period_value: string;  // e.g. "2026-W09"
  mvc_certification_pct_portfolio: number | null;
  [key: string]: unknown;
}

/** Full response shape of GET /api/apmt/terminal-availability */
interface TerminalAvailData {
  periods: AvailPeriod[];
  threshold: number;
  summary_availabilities: (number | null)[];
  terminals: AvailTerminal[];
}

// ─── Week column helpers ──────────────────────────────────────────────────────

/** Return the Monday (ISO date string) of the week that contains `date` */
function getMondayOfWeek(date: Date): string {
  const d = new Date(date);
  const dow = (d.getDay() + 6) % 7; // Mon=0, Sun=6
  d.setDate(d.getDate() - dow);
  return d.toISOString().slice(0, 10);
}

/** Return the ISO week number for a given date */
function getISOWeekNumber(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayOfWeek = (d.getUTCDay() + 6) % 7;
  const thursday  = new Date(d);
  thursday.setUTCDate(d.getUTCDate() - dayOfWeek + 3);
  const firstThursday = new Date(Date.UTC(thursday.getUTCFullYear(), 0, 4));
  return 1 + Math.round((thursday.getTime() - firstThursday.getTime()) / (7 * 24 * 60 * 60 * 1000));
}

/**
 * Convert an ISO year-week string (e.g. "2026-W09") to the Monday ISO date
 * using ISO 8601 rules (week 1 contains Jan 4, week starts on Monday).
 */
function isoWeekToMonday(yearWeek: string): string {
  const parts = yearWeek.split('-W');
  if (parts.length !== 2) return '';
  const year    = parseInt(parts[0], 10);
  const weekNum = parseInt(parts[1], 10);
  if (isNaN(year) || isNaN(weekNum)) return '';
  // Jan 4 is always in ISO week 1
  const jan4    = new Date(Date.UTC(year, 0, 4));
  const jan4Dow = jan4.getUTCDay() || 7; // Mon=1 ... Sun=7
  const monday1 = new Date(jan4);
  monday1.setUTCDate(jan4.getUTCDate() - (jan4Dow - 1));
  const target = new Date(monday1);
  target.setUTCDate(monday1.getUTCDate() + (weekNum - 1) * 7);
  return target.toISOString().slice(0, 10);
}

/**
 * Compute ISO week columns from a fixed start Monday up to (and including)
 * the current week. `startMondayISO` must be a YYYY-MM-DD Monday.
 */
function computeWeekColsFromDate(startMondayISO: string): WeekColumn[] {
  const today          = new Date();
  const currentMonISO  = getMondayOfWeek(today);
  const currentMonDate = new Date(currentMonISO + 'T00:00:00Z');

  const cols: WeekColumn[] = [];
  let cur = new Date(startMondayISO + 'T00:00:00Z');

  while (cur <= currentMonDate) {
    const mondayISO = cur.toISOString().slice(0, 10);
    const weekNum   = getISOWeekNumber(cur);
    const isCurrent = mondayISO === currentMonISO;
    cols.push({
      headerLabel: isCurrent ? `Current Week ${weekNum}` : `Week ${weekNum}`,
      mondayISO,
      weekNum,
      current: isCurrent,
    });
    const next = new Date(cur);
    next.setUTCDate(cur.getUTCDate() + 7);
    cur = next;
  }
  return cols;
}

// ─── Static / Mock data ───────────────────────────────────────────────────────

/**
 * Operations KPIs template.
 * The three "Major Incident Management" rows carry a `mimKey` that tells the
 * renderer which field from the API response to use for each week column.
 * All other rows retain static placeholder values.
 *
 * Static placeholder values are an empty array [] for MIM rows because the
 * component will fill them from the API.  Other rows keep hardcoded arrays so
 * the table still looks meaningful while their backends are being built.
 */
const OPS_KPI_TEMPLATE: KPIRow[] = [
  {
    functionName:  'Terminals Availability Target',
    metric:        'Out of 34, % of Terminals that met availability target',
    targetLabel:   'Not Set',
    unit:          'percent',
    targetValue:   null,
    direction:     'up',
    termAvailKey:  true,
    values:        [], // filled by terminal-availability-weekly API
  },
  {
    functionName:  'Major Incident Management',
    metric:        'Number of P1/P2 incidents',
    targetLabel:   '2',
    unit:          'count',
    targetValue:   2,
    direction:     'down',
    mimKey:        'p1p2_count',
    values:        [], // filled by API
  },
  {
    functionName:  'Major Incident Management',
    metric:        'Cumulative Downtime (mins)',
    targetLabel:   '347',
    unit:          'mins',
    targetValue:   347,
    direction:     'down',
    mimKey:        'cumulative_downtime_mins',
    values:        [], // filled by API
  },
  {
    functionName:  'Major Incident Management',
    metric:        'P1 & P2 MTTR - Median (hours)',
    targetLabel:   '1.8',
    unit:          'hours',
    targetValue:   1.8,
    direction:     'down',
    mimKey:        'mttr_median_hours',
    values:        [], // filled by API
  },
  {
    functionName:  'Change Management',
    metric:        '% P1/P2 caused by change',
    targetLabel:   '20.00%',
    unit:          'percent',
    targetValue:   20,
    direction:     'down',
    mimKey:        'pct_caused_by_change',
    values:        [], // filled by API
  },
  {
    functionName:  'Post Mortem',
    metric:        'PostMortems sent for LTE review (<9 days)',
    targetLabel:   '0',
    unit:          'count',
    targetValue:   0,
    direction:     'down',
    values: [0, 2, 0, 2, 0, 3, 1, 0],
  },
  {
    functionName:  'Maersk Service Atlas (MSA)',
    metric:        '%Apps certified in MSA',
    targetLabel:   '70.00%',
    unit:          'percent',
    targetValue:   70,
    direction:     'up',
    msaKey:        'mvc_certification_pct_portfolio',
    values:        [], // filled by asset-change-metrics API
  },
];

// ─── Colour helpers ───────────────────────────────────────────────────────────

/** Cell colour for Ops KPIs bowler chart */
const kpiCellStyle = (
  row: KPIRow,
  value: CellValue,
): React.CSSProperties => {
  if (value === null || value === undefined)
    return { background: '#f5f5f5', color: '#9e9e9e' };
  if (typeof value === 'string')
    return {};
  if (row.targetValue === null)
    return availCellStyle(value as number, 97); // fallback for "Not Set" rows

  const ok =
    row.direction === 'down'
      ? (value as number) <= row.targetValue
      : (value as number) >= row.targetValue;

  return ok
    ? { background: '#dff0d8', color: '#2e7d32' }
    : { background: '#ffcdd2', color: '#c62828' };
};

/**
 * Cell colour for Terminal Availability.
 * > 99.80 % → green, ≤ 99.80 % → red.
 * An optional `threshold` lets the OpsKPIs fallback row (target=null) use a
 * different threshold (e.g. 97).
 */
const availCellStyle = (
  pct: number,
  threshold = 99.80,
): React.CSSProperties => {
  if (pct > threshold) return { background: '#dff0d8', color: '#2e7d32' }; // green
  return                      { background: '#ffcdd2', color: '#c62828' }; // red
};

// ─── Shared table styling constants ──────────────────────────────────────────

const HDR_STYLE: React.CSSProperties = {
  fontWeight: 700,
  fontSize: '0.78rem',
  background: '#cfe8fc',
  border: '1px solid #b0c4de',
  textAlign: 'center',
  whiteSpace: 'nowrap',
  padding: '6px 10px',
};

const HDR_CURRENT_STYLE: React.CSSProperties = {
  ...HDR_STYLE,
  background: '#0067b3',
  color: '#ffffff',
};

const CELL_BASE: React.CSSProperties = {
  border: '1px solid #e0e6eb',
  fontSize: '0.82rem',
  padding: '5px 10px',
  textAlign: 'center',
};

const FUNC_CELL: React.CSSProperties = {
  ...CELL_BASE,
  fontWeight: 700,
  whiteSpace: 'nowrap',
  background: '#f0f4f8',
  textAlign: 'left',
  color: '#001a35',
};

const METRIC_CELL: React.CSSProperties = {
  ...CELL_BASE,
  textAlign: 'left',
  maxWidth: 260,
  fontSize: '0.8rem',
  color: '#333',
};

const TARGET_CELL: React.CSSProperties = {
  ...CELL_BASE,
  fontWeight: 600,
  background: '#dbeafe',
  color: '#1e40af',
  whiteSpace: 'nowrap',
};

// ─── Format helpers ───────────────────────────────────────────────────────────

/** Format a KPI cell value according to its unit */
function formatKPIValue(row: KPIRow, val: CellValue): string {
  if (val === null || val === undefined) return 'NA';
  if (typeof val === 'string')           return val;
  const n = val as number;
  if (row.unit === 'percent') return `${n.toFixed(2)}%`;
  if (row.unit === 'hours')   return n.toFixed(2);
  if (row.unit === 'mins')    return String(Math.round(n));
  return String(n);
}

// ─── Ops KPIs tab (live MIM data) ────────────────────────────────────────────

function OpsKPIsTab() {
  // Compute week columns from ISO Week 1 of 2026 (Mon 2025-12-29) to today
  const weekCols = useMemo(() => computeWeekColsFromDate('2025-12-29'), []);

  // ── MIM data fetch state ────────────────────────────────────────────────
  const [mimMap, setMimMap]        = useState<Record<string, MIMKPIPoint>>({});
  const [mimLoading, setMimLoading] = useState(true);
  const [mimError, setMimError]    = useState<string | null>(null);

  const fetchMimData = () => {
    setMimLoading(true);
    setMimError(null);
    const start = '2025-12-29';
    const end   = new Date().toISOString().slice(0, 10);
    authFetch(`${API_BASE_URL}/api/apmt/major-incident-kpis?start=${start}&end=${end}`)
      .then(res => res.json())
      .then((data: { success: boolean; data: MIMKPIPoint[]; error?: string }) => {
        if (!data.success) throw new Error(data.error ?? 'API error');
        const map: Record<string, MIMKPIPoint> = {};
        for (const pt of data.data) {
          map[pt.period_start] = pt;
        }
        setMimMap(map);
      })
      .catch(err => {
        console.error('[APMTIncidentOperations] MIM KPI fetch error:', err);
        setMimError(String(err));
      })
      .finally(() => setMimLoading(false));
  };

  // ── Terminal availability weekly fetch state ─────────────────────────────
  // Keyed by Monday ISO date
  const [termAvailMap, setTermAvailMap]        = useState<Record<string, number | null>>({});
  const [termAvailLoading, setTermAvailLoading] = useState(true);
  const [termAvailError, setTermAvailError]    = useState<string | null>(null);

  const fetchTermAvailData = () => {
    setTermAvailLoading(true);
    setTermAvailError(null);
    authFetch(`${API_BASE_URL}/api/apmt/terminal-availability-weekly`)
      .then(res => res.json())
      .then((data: { success: boolean; data: TermAvailWeekPoint[]; error?: string }) => {
        if (!data.success) throw new Error(data.error ?? 'API error');
        const map: Record<string, number | null> = {};
        for (const pt of data.data) {
          map[pt.period_start] = pt.pct_meeting_target;
        }
        setTermAvailMap(map);
      })
      .catch(err => {
        console.error('[APMTIncidentOperations] terminal-availability-weekly fetch error:', err);
        setTermAvailError(String(err));
      })
      .finally(() => setTermAvailLoading(false));
  };

  // ── MSA (asset-change-metrics) fetch state ──────────────────────────────
  // Keyed by Monday ISO date (computed from period_value like "2026-W09")
  const [msaMap, setMsaMap]        = useState<Record<string, number>>({});
  const [msaLoading, setMsaLoading] = useState(true);
  const [msaError, setMsaError]    = useState<string | null>(null);

  const fetchMsaData = () => {
    const nocBase = process.env.REACT_APP_NOC_DASHBOARD_API;
    if (!nocBase) {
      setMsaLoading(false);
      setMsaError('REACT_APP_NOC_DASHBOARD_API not configured');
      return;
    }
    setMsaLoading(true);
    setMsaError(null);
    authFetch(`${nocBase}/service-deck/asset-change-metrics`)
      .then(res => res.json())
      .then((resp: { data?: AssetChangeMetricItem[] }) => {
        const map: Record<string, number> = {};
        (resp.data ?? []).forEach(item => {
          if (
            item.portfolio === 'APMT, IOT and Automation' &&
            item.period_type === 'WEEK' &&
            item.mvc_certification_pct_portfolio !== null
          ) {
            const monday = isoWeekToMonday(item.period_value);
            if (monday) map[monday] = item.mvc_certification_pct_portfolio as number;
          }
        });
        setMsaMap(map);
      })
      .catch(err => {
        console.error('[APMTIncidentOperations] MSA fetch error:', err);
        setMsaError(String(err));
      })
      .finally(() => setMsaLoading(false));
  };

  useEffect(() => {
    fetchMimData();
    fetchMsaData();
    fetchTermAvailData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Build final rows ────────────────────────────────────────────────────
  const kpiRows: KPIRow[] = useMemo(() => {
    const numCols = weekCols.length;
    return OPS_KPI_TEMPLATE.map(row => {
      // Terminal availability weekly row
      if (row.termAvailKey) {
        const values: CellValue[] = weekCols.map(col => {
          const v = termAvailMap[col.mondayISO];
          return v !== undefined ? v : null;
        });
        return { ...row, values };
      }

      // MSA row: keyed by monday ISO in msaMap
      if (row.msaKey) {
        const values: CellValue[] = weekCols.map(col => {
          const v = msaMap[col.mondayISO];
          return v !== undefined ? v : null;
        });
        return { ...row, values };
      }

      // MIM rows: keyed by monday ISO in mimMap
      if (row.mimKey) {
        const values: CellValue[] = weekCols.map(col => {
          const pt = mimMap[col.mondayISO];
          if (!pt) return null;
          const v = pt[row.mimKey!];
          return v !== undefined ? v : null;
        });
        return { ...row, values };
      }

      // Static rows: pad / trim to numCols
      const padded = new Array<CellValue>(numCols).fill(null);
      for (let i = 0; i < numCols; i++) {
        padded[i] = row.values[i] !== undefined ? row.values[i] : null;
      }
      return { ...row, values: padded };
    });
  }, [weekCols, mimMap, msaMap, termAvailMap]);

  // ── Build rowspan map for merged "Functions" column ─────────────────────
  const rowspanMap      = useMemo(() => {
    const m = new Map<string, number>();
    kpiRows.forEach(r => m.set(r.functionName, (m.get(r.functionName) ?? 0) + 1));
    return m;
  }, [kpiRows]);

  const firstOccurrence = useMemo(() => {
    const m = new Map<string, number>();
    kpiRows.forEach((r, i) => { if (!m.has(r.functionName)) m.set(r.functionName, i); });
    return m;
  }, [kpiRows]);

  return (
    <Box>
      {/* Status bar: loading / error / refresh */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1, pt: 0.5, flexWrap: 'wrap' }}>
        {(mimLoading || msaLoading || termAvailLoading) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="caption" color="text.secondary">Loading KPI data…</Typography>
          </Box>
        )}
        {mimError && !mimLoading && (
          <Alert severity="warning" sx={{ py: 0, fontSize: '0.78rem' }}>MIM: {mimError}</Alert>
        )}
        {msaError && !msaLoading && (
          <Alert severity="warning" sx={{ py: 0, fontSize: '0.78rem' }}>MSA: {msaError}</Alert>
        )}
        {termAvailError && !termAvailLoading && (
          <Alert severity="warning" sx={{ py: 0, fontSize: '0.78rem' }}>Terminal Availability: {termAvailError}</Alert>
        )}
        {!mimLoading && !msaLoading && !termAvailLoading && !mimError && !msaError && !termAvailError && (
          <Typography variant="caption" color="text.secondary">
            MIM rows from database · MSA from asset-change-metrics API · Terminal Availability from DB.
          </Typography>
        )}
        <Tooltip title="Refresh all data">
          <IconButton size="small" onClick={() => { fetchMimData(); fetchMsaData(); fetchTermAvailData(); }} disabled={mimLoading || msaLoading || termAvailLoading}>
            <Refresh fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <TableContainer component={Paper} sx={{ mt: 0 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell style={{ ...HDR_STYLE, textAlign: 'left', minWidth: 160 }}>Functions</TableCell>
              <TableCell style={{ ...HDR_STYLE, textAlign: 'left', minWidth: 220 }}>Metrics</TableCell>
              <TableCell style={{ ...HDR_STYLE, minWidth: 80 }}></TableCell>
              {weekCols.map(col => (
                <TableCell
                  key={col.mondayISO}
                  style={col.current ? HDR_CURRENT_STYLE : HDR_STYLE}
                >
                  {col.headerLabel}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {kpiRows.map((row, rowIdx) => {
              const isFirst    = firstOccurrence.get(row.functionName) === rowIdx;
              const span       = rowspanMap.get(row.functionName) ?? 1;
              const rowIsLive  = !!row.mimKey || !!row.msaKey || !!row.termAvailKey;
              const rowLoading = (row.mimKey && mimLoading) || (row.msaKey && msaLoading) || (row.termAvailKey && termAvailLoading);

              return (
                <React.Fragment key={`${row.functionName}-${rowIdx}`}>
                  {/* ── Target row ── */}
                  <TableRow sx={{ '&:hover td': { filter: 'brightness(0.96)' } }}>
                    {isFirst && (
                      <TableCell rowSpan={span * 2} style={FUNC_CELL}>
                        {row.functionName}
                      </TableCell>
                    )}
                    <TableCell rowSpan={2} style={METRIC_CELL}>{row.metric}</TableCell>
                    <TableCell style={{ ...TARGET_CELL, fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase' as const }}>
                      Target
                    </TableCell>
                    {weekCols.map((col, colIdx) => (
                      <TableCell
                        key={colIdx}
                        style={{
                          ...CELL_BASE,
                          background: '#dbeafe',
                          color: '#1e40af',
                          fontWeight: 600,
                          outline: col.current ? '2px solid #0067b3' : undefined,
                        }}
                      >
                        {row.targetLabel}
                      </TableCell>
                    ))}
                  </TableRow>

                  {/* ── Actuals row ── */}
                  <TableRow sx={{ '&:hover td': { filter: 'brightness(0.97)' } }}>
                    <TableCell style={{
                      ...TARGET_CELL,
                      background: '#f0fdf4',
                      color: '#166534',
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      letterSpacing: '0.05em',
                      textTransform: 'uppercase' as const,
                      borderBottom: '2px solid #cbd5e1',
                    }}>
                      Actuals
                    </TableCell>
                    {row.values.map((val, colIdx) => {
                      const isCurrent = weekCols[colIdx]?.current;
                      const colStyle  = kpiCellStyle(row, val);
                      const display   = rowLoading
                        ? '…'
                        : (val === null ? 'NA' : formatKPIValue(row, val));

                      return (
                        <TableCell
                          key={colIdx}
                          style={{
                            ...CELL_BASE,
                            ...(display === 'NA' || display === '…' ? { background: '#f5f5f5', color: '#9e9e9e' } : colStyle),
                            fontWeight: isCurrent ? 700 : 500,
                            outline: isCurrent ? '2px solid #0067b3' : undefined,
                            fontStyle: rowIsLive ? 'normal' : undefined,
                            borderBottom: '2px solid #cbd5e1',
                          }}
                        >
                          {display}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

// ─── Terminal Availability tab ────────────────────────────────────────────────

function TerminalAvailabilityTab() {
  const [availData, setAvailData]   = useState<TerminalAvailData | null>(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState<string | null>(null);

  const fetchData = () => {
    setLoading(true);
    setError(null);
    authFetch(`${API_BASE_URL}/api/apmt/terminal-availability`)
      .then(res => res.json())
      .then((resp: { success: boolean; data: TerminalAvailData; error?: string }) => {
        if (!resp.success) throw new Error(resp.error ?? 'API error');
        setAvailData(resp.data);
      })
      .catch(err => {
        console.error('[APMTIncidentOperations] terminal-availability fetch error:', err);
        setError(String(err));
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const regionTerminals = useMemo(() => {
    const map = new Map<string, AvailTerminal[]>();
    for (const t of availData?.terminals ?? []) {
      const list = map.get(t.region) ?? [];
      list.push(t);
      map.set(t.region, list);
    }
    return map;
  }, [availData]);

  const regions      = useMemo(() => Array.from(regionTerminals.keys()), [regionTerminals]);
  const periods      = availData?.periods ?? [];
  const summary      = availData?.summary_availabilities ?? [];
  const threshold    = availData?.threshold ?? 99.80;
  const termCount    = availData?.terminals.length ?? 0;
  const currentIdx   = periods.findIndex(p => p.current);

  return (
    <Box>
      {/* Status bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1, pt: 0.5 }}>
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="caption" color="text.secondary">
              Loading terminal availability…
            </Typography>
          </Box>
        )}
        {error && !loading && (
          <Alert severity="warning" sx={{ py: 0, fontSize: '0.78rem', flex: 1 }}>
            Could not load terminal availability: {error}
          </Alert>
        )}
        {!loading && !error && availData && (
          <Typography variant="caption" color="text.secondary">
            Availability sourced live from the database ({termCount} terminals). Threshold: &gt;{threshold}% = green.
          </Typography>
        )}
        <Tooltip title="Refresh availability data">
          <IconButton size="small" onClick={fetchData} disabled={loading}>
            <Refresh fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <TableContainer component={Paper} sx={{ mt: 0 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell style={{ ...HDR_STYLE, textAlign: 'left', minWidth: 80 }}>Region</TableCell>
              <TableCell style={{ ...HDR_STYLE, textAlign: 'left', minWidth: 220 }}>Terminals</TableCell>
              {periods.map((p, idx) => (
                <TableCell
                  key={p.label}
                  style={p.current ? HDR_CURRENT_STYLE : HDR_STYLE}
                >
                  {p.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {/* Summary row: % of terminals that met the threshold */}
            <TableRow sx={{ background: '#f0f4f8' }}>
              <TableCell style={{ ...FUNC_CELL, fontStyle: 'italic' }} colSpan={1} />
              <TableCell
                style={{ ...METRIC_CELL, fontWeight: 700, color: '#001a35', background: '#f0f4f8' }}
              >
                Terminal Availability (Out of {termCount})
              </TableCell>
              {loading
                ? periods.map((_, idx) => (
                    <TableCell key={idx} style={{ ...CELL_BASE, background: '#f5f5f5', color: '#9e9e9e' }}>…</TableCell>
                  ))
                : summary.map((val, idx) => (
                    <TableCell
                      key={idx}
                      style={{
                        ...CELL_BASE,
                        ...(val !== null ? availCellStyle(val, threshold) : { background: '#f5f5f5', color: '#9e9e9e' }),
                        fontWeight: idx === currentIdx ? 700 : 600,
                        outline: idx === currentIdx ? '2px solid #0067b3' : undefined,
                      }}
                    >
                      {val !== null ? `${val.toFixed(2)}%` : 'N/A'}
                    </TableCell>
                  ))
              }
            </TableRow>

            {/* Loading skeleton */}
            {loading && (
              <TableRow>
                <TableCell colSpan={2 + periods.length} align="center" sx={{ py: 3 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            )}

            {/* Per-region terminal rows */}
            {!loading && regions.map(region => {
              const rows = regionTerminals.get(region) ?? [];
              return rows.map((term, rIdx) => (
                <TableRow
                  key={term.id}
                  sx={{ '&:hover': { background: '#f2f7fa' } }}
                >
                  {/* Merged Region cell */}
                  {rIdx === 0 && (
                    <TableCell
                      rowSpan={rows.length}
                      style={{ ...FUNC_CELL, verticalAlign: 'middle', textAlign: 'center' }}
                    >
                      {region}
                    </TableCell>
                  )}

                  {/* Terminal name */}
                  <TableCell style={METRIC_CELL}>{term.display_name}</TableCell>

                  {/* Availability per period */}
                  {term.availabilities.map((val, colIdx) => (
                    <TableCell
                      key={colIdx}
                      style={{
                        ...CELL_BASE,
                        ...(val !== null
                          ? availCellStyle(val, threshold)
                          : { background: '#f5f5f5', color: '#9e9e9e' }),
                        fontWeight: colIdx === currentIdx ? 700 : 500,
                        outline: colIdx === currentIdx ? '2px solid #0067b3' : undefined,
                      }}
                    >
                      {val !== null ? `${val.toFixed(2)}%` : 'N/A'}
                    </TableCell>
                  ))}
                </TableRow>
              ));
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function APMTIncidentOperations() {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <Box sx={{ p: 2 }}>
      {/* Page header */}
      <Typography variant="h5" sx={{ fontWeight: 700, color: '#001a35', mb: 1 }}>
        APMT Incident &amp; Operations
      </Typography>
      <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
        Weekly bowler chart view of operational KPIs and terminal availability for Q2 2026.
      </Typography>

      {/* Tab bar */}
      <Paper variant="outlined" sx={{ mb: 0 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{
            borderBottom: '1px solid #e0e6eb',
            '& .MuiTab-root': { fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' },
          }}
        >
          <Tab label="Operations KPIs" />
          <Tab label="Terminal Availability" />
        </Tabs>
      </Paper>

      {/* Tab panels */}
      <Box sx={{ mt: 0 }}>
        {activeTab === 0 && <OpsKPIsTab />}
        {activeTab === 1 && <TerminalAvailabilityTab />}
      </Box>
    </Box>
  );
}
